/**
 * GeolocationContext — Marketplace-grade location state
 *
 * Sources:
 *   "ip"      – IP-based approximate city (auto on mount, no permission)
 *   "precise" – navigator.geolocation (user-triggered only)
 *   "manual"  – User selected a city from the selector
 *
 * Lifecycle:
 *   1. On mount → restore from localStorage, or detect via IP
 *   2. User clicks "Detect Current Location" → browser prompt (user gesture)
 *   3. User selects city via CitySelector → manual source
 *
 * Never calls navigator.geolocation on page load.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  detectLocationByIP,
  reverseGeocode,
} from '../services/location.service';

// ─── Types ────────────────────────────────────────────────────
export type LocationSource = 'ip' | 'precise' | 'manual';

export interface UserLocation {
  city: string;
  latitude: number;
  longitude: number;
  source: LocationSource;
}

interface GeolocationContextValue {
  /** Current resolved location (always non-null after init) */
  location: UserLocation | null;
  /** True while detecting / fetching location */
  loading: boolean;
  /** Human-readable error (toast-friendly) */
  error: string | null;
  /** True if browser denied geolocation permission */
  permissionDenied: boolean;
  /** Browser supports navigator.geolocation */
  supported: boolean;
  /** Request precise GPS location (user-triggered) */
  requestPreciseLocation: (onError?: (message: string) => void) => void;
  /** Set location manually by city name + coords */
  setManualLocation: (city: string, lat: number, lng: number) => void;
}

const GeolocationContext = createContext<GeolocationContextValue | undefined>(
  undefined,
);

// ─── localStorage persistence ─────────────────────────────────
const STORAGE_KEY = 'appointigo_location';

const loadPersistedLocation = (): UserLocation | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UserLocation;
    if (parsed.city && parsed.latitude && parsed.longitude && parsed.source) {
      return parsed;
    }
  } catch { /* ignore */ }
  return null;
};

const persistLocation = (loc: UserLocation): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
  } catch { /* ignore */ }
};

// // ─── Types ────────────────────────────────────────────────────
export const GeolocationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [location, setLocation] = useState<UserLocation | null>(
    () => loadPersistedLocation(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const permissionDenied = false; // kept for interface compatibility

  const supported =
    typeof navigator !== 'undefined' && 'geolocation' in navigator;

  // ── 1. Initialise: persisted → check permission → IP fallback ──
  useEffect(() => {
    // If we already have precise location persisted, nothing to do
    if (location?.source === 'precise') return;

    let cancelled = false;

    const upgradeToPrecise = () => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          if (cancelled) return;
          const { latitude, longitude } = position.coords;
          const city = await reverseGeocode(latitude, longitude);
          const loc: UserLocation = {
            city: city || 'Your Location',
            latitude,
            longitude,
            source: 'precise',
          };
          setLocation(loc);
          persistLocation(loc);
          setLoading(false);
        },
        () => {
          // Precise failed even with permission — keep existing or fall back to IP
          if (cancelled) return;
          if (!location) {
            detectViaIP();
          } else {
            setLoading(false);
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
      );
    };

    const detectViaIP = () => {
      detectLocationByIP().then((detected) => {
        if (cancelled) return;
        const loc: UserLocation = {
          city: detected.city,
          latitude: detected.latitude,
          longitude: detected.longitude,
          source: 'ip',
        };
        setLocation(loc);
        persistLocation(loc);
        setLoading(false);
      }).catch(() => {
        if (cancelled) return;
        const loc: UserLocation = {
          city: 'Lucknow',
          latitude: 26.8467,
          longitude: 80.9462,
          source: 'ip',
        };
        setLocation(loc);
        persistLocation(loc);
        setLoading(false);
      });
    };

    const init = async () => {
      setLoading(true);

      // Check if geolocation permission is already granted
      try {
        if (supported && navigator.permissions) {
          const status = await navigator.permissions.query({ name: 'geolocation' });
          if (status.state === 'granted' && !cancelled) {
            // Permission already granted — upgrade to precise
            upgradeToPrecise();
            return;
          }
        }
      } catch { /* permissions API not available, fall through */ }

      // No permission granted — use persisted location or detect via IP
      if (!cancelled) {
        if (location) {
          // Already have an IP/manual location, just use it
          setLoading(false);
        } else {
          detectViaIP();
        }
      }
    };

    init();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 2. Precise location (user-triggered) ──────────────────────────────
  const requestPreciseLocation = useCallback(async (onError?: (message: string) => void) => {
    if (!supported) {
      const msg = 'Geolocation is not supported by your browser';
      setError(msg);
      onError?.(msg);
      return;
    }

    // Check permission state first — if already denied, tell
    // the user to fix it in settings instead of calling
    // getCurrentPosition (which would silently fail without a popup).
    try {
      if (navigator.permissions) {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        if (status.state === 'denied') {
          const msg = 'Location permission is blocked. Please enable it from your browser site settings and try again!';
          setError(msg);
          onError?.(msg);
          return;
        }
      }
    } catch { /* permissions API unavailable — proceed normally */ }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Reverse geocode to get city name
        const city = await reverseGeocode(latitude, longitude);

        const loc: UserLocation = {
          city: city || location?.city || 'Your Location',
          latitude,
          longitude,
          source: 'precise',
        };
        setLocation(loc);
        persistLocation(loc);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        let msg: string;
        switch (err.code) {
          case err.PERMISSION_DENIED:
            msg = 'Please enable location permission from settings and try again!';
            break;
          case err.POSITION_UNAVAILABLE:
            msg = 'Location information is unavailable.';
            break;
          case err.TIMEOUT:
            msg = 'Location request timed out. Please try again.';
            break;
          default:
            msg = 'An error occurred while getting your location.';
        }
        setError(msg);
        onError?.(msg);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      },
    );
  }, [supported, location?.city]);

  // ── 3. Manual city selection ──────────────────────────────────────────
  const setManualLocation = useCallback(
    (city: string, lat: number, lng: number) => {
      const loc: UserLocation = {
        city,
        latitude: lat,
        longitude: lng,
        source: 'manual',
      };
      setLocation(loc);
      persistLocation(loc);
      setError(null);
    },
    [],
  );

  // ── Context value ──────────────────────────────────────────────────────
  const value = useMemo(
    () => ({
      location,
      loading,
      error,
      permissionDenied,
      supported,
      requestPreciseLocation,
      setManualLocation,
    }),
    [
      location,
      loading,
      error,
      permissionDenied,
      supported,
      requestPreciseLocation,
      setManualLocation,
    ],
  );

  return (
    <GeolocationContext.Provider value={value}>
      {children}
    </GeolocationContext.Provider>
  );
};

// ── Hook ────────────────────────────────────────────────────────────────
export const useGeolocation = (): GeolocationContextValue => {
  const context = useContext(GeolocationContext);
  if (context === undefined) {
    throw new Error('useGeolocation must be used within a GeolocationProvider');
  }
  return context;
};

export default GeolocationContext;
